<template>
  <div>
    <div class="table-wrapper">
      <table class="table">
        <tr>
          <th><!-- Deliberately empty --></th>
          <th v-for="( preValue ) in preValues"
              :key="preValue.id"
              :class="{ invalid: showValidation && !isValid }">
            {{ preValue.name }}
          </th>
        </tr>

        <tr v-for="( matrixValue ) in matrixValues"
            :key="matrixValue.id">
          <th :class="{ invalid: showValidation && !isValid }">
            {{ matrixValue.name }}
          </th>

          <td v-for="( preValue ) in preValues"
              :key="preValue.id">
            <div class="checkbox">
              <input :id="`intersection_${getIntersection( preValue.id, matrixValue.id ).value}`"
                     v-model="options[matrixValue.id].values"
                     :value="{ name: preValue.name, id: preValue.id }"
                     type="checkbox"
                     :aria-label="`${matrixValue.name} ${matrixValue.name}`">
              <label :for="`intersection_${getIntersection( preValue.id, matrixValue.id ).value}`">
              </label>
            </div>
          </td>
        </tr>
      </table>
    </div>

    <div v-if="showValidation && !isValid"
         class="validation-message">
      <span>Required</span>
    </div>
  </div>
</template>

<script>

import vmodelMixin from "~/mixins/vmodelMixin";
import fieldMixin from "~/mixins/fieldMixin";
import validationMixin from "~/mixins/validationMixin";

export default {
  name: "MatrixField",
  mixins: [vmodelMixin, fieldMixin, validationMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    matrixValues: {
      type: Array,
      default: () => []
    },
    matrixIntersections: {
      type: Array,
      default: () => []
    }
  },
  data: () => ( {
    mutable: null,
    options: null
  } ),
  computed: {
    isValid () {
      let result = true;

      if ( this.required && this.options ) {
        result = Object.values( this.options ).some(
          x => x.values && x.values.length > 0
        );
      }

      return result;
    }
  },
  watch: {
    options: {
      handler ( val ) {
        this.copyToMutable( val );
        this.validate();
      },
      deep: true
    },
    mutable: {
      handler ( val ) {
        this.copyFromMutable( val );
      }
    }
  },
  created () {
    this.createFieldOptions();
    this.copyFromMutable( this.mutable );
  },
  methods: {
    createFieldOptions () {
      const result = {};

      this.matrixValues.forEach( matrixValue => {
        result[matrixValue.id] = {
          name: matrixValue.name,
          values: []
        };
      } );

      this.options = result;
    },
    copyToMutable ( options ) {
      if ( !this.mutable.fieldValues ) {
        this.mutable.fieldValues = [];
      }

      // Remove all existing values
      const toRemove = this.mutable.fieldValues.filter( x => x.name === this.name );
      toRemove.forEach( x => {
        const index = this.mutable.fieldValues.indexOf( x );
        this.mutable.fieldValues.splice( index, 1 );
      } );

      // Iterate through any current values and re-add them
      if ( options ) {
        Object.entries( options ).forEach( ( [key, value] ) => {
          if ( value ) {
            value.values.forEach( innerValue => {
              const intersection = this.getIntersection( innerValue.id, key );
              let intersectionId = null;
              if ( intersection ) {
                intersectionId = intersection.value;
              }

              const compositeValue = `${innerValue.name}|${value.name}`;

              this.mutable.fieldValues.push( {
                fieldId: this.id,
                name: this.name,
                itemId: intersectionId,
                value: compositeValue
              } );
            } );
          }
        } );
      }
    },
    copyFromMutable ( mutable ) {
      const { fieldValues } = mutable;

      let matchingFieldValues = [];
      if ( fieldValues ) {
        matchingFieldValues = fieldValues.filter( x => x.name === this.name );
      }

      if ( matchingFieldValues ) {
        matchingFieldValues.forEach( match => {
          const intersection = this.matrixIntersections.find( x => x.value === match.itemId );
          if ( intersection ) {
            const preValue = this.preValues.find( x => x.id === intersection.x );

            const exists = this.options[intersection.y].values.find( x => x.id === preValue.id );
            if ( !exists ) {
              this.options[intersection.y].values.push( preValue );
            }
          }
        } );
      }
    },
    getIntersection ( x, y ) {
      return this.matrixIntersections.find( z => z.x === x && z.y === y );
    }
  }
};
</script>

<style lang="less" scoped>
th.invalid {
  color: red;
}
label {
  height: 16px;
}
.table-wrapper {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
@media( max-width: 575px ) {
  .table-wrapper {
    max-width: 85vw;
  }
}
</style>
